import { gsap, ScrollTrigger } from "../LibrariesImport";
import { GlobalVariables } from "../GlobalVariables";
import followCursorBadge from "../utils/followCursorBadge";

const SCROLL_PER_SLIDE_VH = 0.4;
// Pin GSAP + swiper synchronisé : uniquement au-dessus du breakpoint lg (cf. .block-solutions-bg
// et .block-solutions-stage.is-staged dans _block-solutions.scss). En dessous, block-solutions.twig
// affiche .block-solutions-mobile (liste statique, une carte 3D par solution), sans JS dédié ici.
const DESKTOP_QUERY = "(min-width: 992px)";

export default class BlockSolutions {
	constructor() {
		const blocks = document.querySelectorAll(".block-solutions");
		if (!blocks.length) return;

		blocks.forEach((block) => {
			const stage = block.querySelector(".block-solutions-stage");
			const leftCol = block.querySelector(".block-solutions-left");
			const swiperEl = block.querySelector(".block-solutions-swiper");
			if (!stage || !leftCol || !swiperEl) return;

			const slidesCount = swiperEl.querySelectorAll(".swiper-slide").length;
			if (!slidesCount) return;

			// La carte 3D renvoie vers la solution qu'elle affiche : le clic est pris en charge par
			// DataLink, qui lit data-link au moment du clic — il suffit donc de réécrire l'attribut à
			// chaque changement de slide. Les liens viennent de la liste .solutions-3d-data, déjà dans
			// le même ordre que les slides et que les cartes du carrousel 3D.
			const canvas = block.querySelector("[data-solutions-3d]");
			const cardLinks = [...block.querySelectorAll("[data-solutions-card]")].map((card) => card.dataset.link);
			const setCardLink = (index) => {
				if (canvas && cardLinks[index]) canvas.dataset.link = cardLinks[index];
			};

			followCursorBadge(canvas, block.querySelector(".cursor-badge"));

			let instance = null;
			let pendingIndex = null;
			let pendingContinuous = null;

			window.addEventListener("solutionsCards3dReady", (e) => {
				if (e.detail.root !== block) return;
				instance = e.detail.instance;
				if (pendingIndex !== null) instance.setActiveIndex(pendingIndex, false);
				if (pendingContinuous !== null) instance.setContinuousIndex(pendingContinuous);
			});

			const setActiveIndex = (index) => {
				if (instance) instance.setActiveIndex(index);
				else pendingIndex = index;
			};

			const setContinuousIndex = (value) => {
				if (instance) instance.setContinuousIndex(value);
				else pendingContinuous = value;
			};

			// Le pin GSAP + le swiper synchronisé au scroll ne concernent que le desktop (cf.
			// .block-solutions-bg et .block-solutions-stage.is-staged, lg+ dans _block-solutions.scss) :
			// gsap.matchMedia recrée toute cette mécanique en repassant au-dessus du breakpoint, et la
			// détruit proprement en repassant dessous — resize traversant lg dans un sens ou l'autre.
			gsap.matchMedia().add(DESKTOP_QUERY, () => {
				// prefers-reduced-motion : pas de scroll-jacking même en desktop, un swiper autoplay
				// simple suffit à faire défiler les slides.
				if (GlobalVariables.reducedMotion) {
					const swiper = new Swiper(swiperEl, {
						direction: "vertical",
						loop: true,
						effect: "fade",
						fadeEffect: { crossFade: true },
						autoplay: { delay: 4000, disableOnInteraction: false },
						on: {
							slideChangeTransitionStart(s) {
								setActiveIndex(s.realIndex);
								setCardLink(s.realIndex);
							},
						},
					});

					return () => swiper.destroy(true, true);
				}

				stage.classList.add("is-staged");

				const swiper = new Swiper(swiperEl, {
					direction: "vertical",
					loop: false,
					allowTouchMove: false,
					effect: "fade",
					fadeEffect: { crossFade: true },
				});

				const slidesDistance = () => slidesCount * window.innerHeight * SCROLL_PER_SLIDE_VH;

				// Le pin doit s'engager juste sous le header fixe (+ une marge), pas au ras du viewport :
				// sans ça la carte pinnée démarre en partie masquée derrière le header.
				const header = document.querySelector(".header-main");
				const pinOffset = () => {
					const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
					const spacerM = parseFloat(getComputedStyle(document.documentElement).fontSize); // spacer-6 = 1rem
					return headerBottom + spacerM;
				};

				let currentIndex = 0;
				const pinTrigger = ScrollTrigger.create({
					trigger: stage,
					start: () => "top top+=" + pinOffset(),
					end: () => "+=" + slidesDistance(),
					pin: stage,
					pinType: "fixed",
					scrub: true,
					// Le pinSpacing ajoute plusieurs centaines de vh au document : tous les triggers
					// situés plus bas dans la page doivent être mesurés APRÈS lui. DataAnim étant
					// instancié avant ce bloc (App.js), ses triggers seraient calés trop haut sans
					// cette priorité de refresh.
					refreshPriority: 1,
					onEnter: () => block.classList.add("is-pinned"),
					onEnterBack: () => block.classList.add("is-pinned"),
					onLeave: () => block.classList.remove("is-pinned"),
					onLeaveBack: () => block.classList.remove("is-pinned"),
					onUpdate(self) {
						const continuousProgress = self.progress;
						setContinuousIndex(continuousProgress * (slidesCount - 1));

						const index = Math.min(slidesCount - 1, Math.floor(continuousProgress * slidesCount));
						if (index === currentIndex) return;
						currentIndex = index;
						swiper.slideTo(index);
						setActiveIndex(index);
						setCardLink(index);
					},
				});

				// La rotation 3D doit rester visible tant que le bloc est à l'écran, pas seulement pendant
				// le pin : ces deux triggers prolongent le mouvement en entrée et en sortie, raccordés sans
				// saut sur les bornes exactes du pin (pinTrigger.start/end) pour ne pas casser la continuité.
				ScrollTrigger.create({
					trigger: block,
					start: "top bottom",
					end: () => pinTrigger.start,
					scrub: true,
					onUpdate: (self) => setContinuousIndex(self.progress - 1),
				});

				ScrollTrigger.create({
					trigger: block,
					start: () => pinTrigger.end,
					end: "bottom top",
					scrub: true,
					onUpdate: (self) => setContinuousIndex(slidesCount - 1 + self.progress),
				});

				// Les ScrollTrigger créés dans ce contexte matchMedia sont déjà tués automatiquement par
				// GSAP en repassant sous lg ; seuls le swiper (tiers, non suivi par GSAP) et les classes
				// posées à la main doivent être nettoyés explicitement.
				return () => {
					block.classList.remove("is-pinned");
					stage.classList.remove("is-staged");
					swiper.destroy(true, true);
				};
			});
		});
	}
}
